import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';

/**
 * Advanced glassmorphism card styles
 * Modern, iOS-inspired card designs with depth and texture
 */

export const getGlassCardStyles = (theme: Theme, variant: 'default' | 'elevated' | 'subtle' = 'default', disableHover: boolean = false): SxProps => {
  const isDark = theme.palette.mode === 'dark';
  const bgColor = isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.5)';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)';
  const whiteOverlay = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)';
  
  const baseStyles: SxProps = {
    backgroundColor: bgColor,
    backdropFilter: 'blur(25px) saturate(180%)',
    WebkitBackdropFilter: 'blur(25px) saturate(180%)',
    border: `1px solid ${borderColor}`,
    borderRadius: '24px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, 
        transparent 0%, 
        ${theme.palette.primary.main}30 20%, 
        ${whiteOverlay} 50%, 
        ${theme.palette.secondary.main}30 80%, 
        transparent 100%
      )`,
      opacity: 0.9,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}08 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}06 0%, transparent 50%),
        linear-gradient(135deg, ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)'} 0%, transparent 50%)
      `,
      pointerEvents: 'none',
      opacity: 0.8,
      zIndex: 0,
    },
  };

  const variants = {
    default: {
      ...baseStyles,
      boxShadow: isDark
        ? `
          0 12px 48px rgba(0, 0, 0, 0.4),
          0 4px 16px rgba(0, 0, 0, 0.3),
          0 2px 8px rgba(0, 0, 0, 0.2),
          inset 0 1px 0 rgba(255, 255, 255, 0.1),
          inset 0 -1px 0 rgba(0, 0, 0, 0.2)
        `
        : `
          0 12px 48px rgba(0, 0, 0, 0.12),
          0 4px 16px rgba(0, 0, 0, 0.08),
          0 2px 8px rgba(0, 0, 0, 0.04),
          0 0 0 1px rgba(255, 255, 255, 0.1) inset,
          inset 0 1px 0 rgba(255, 255, 255, 0.6),
          inset 0 -1px 0 rgba(255, 255, 255, 0.1)
        `,
      ...(disableHover ? {} : {
        '&:hover': {
          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          border: `1px solid ${theme.palette.primary.main}30`,
          boxShadow: isDark
            ? `
              0 20px 64px rgba(0, 0, 0, 0.5),
              0 12px 32px ${theme.palette.primary.main}30,
              0 6px 16px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.15),
              inset 0 -1px 0 rgba(0, 0, 0, 0.3)
            `
            : `
              0 20px 64px rgba(0, 0, 0, 0.15),
              0 12px 32px ${theme.palette.primary.main}18,
              0 6px 16px rgba(0, 0, 0, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.7),
              inset 0 -1px 0 rgba(255, 255, 255, 0.2)
            `,
          transform: 'translateY(-6px) scale(1.01)',
          '&::before': {
            opacity: 1,
            height: '4px',
          },
        },
      }),
    },
    elevated: {
      ...baseStyles,
      backgroundColor: isDark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(30px) saturate(180%)',
      WebkitBackdropFilter: 'blur(30px) saturate(180%)',
      border: `1px solid ${theme.palette.primary.main}25`,
      boxShadow: `
        0 20px 64px rgba(0, 0, 0, 0.12),
        0 12px 32px ${theme.palette.primary.main}15,
        0 6px 20px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.7),
        inset 0 -1px 0 rgba(255, 255, 255, 0.2)
      `,
      ...(disableHover ? {} : {
        '&:hover': {
          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.85)' : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(35px) saturate(180%)',
          WebkitBackdropFilter: 'blur(35px) saturate(180%)',
          border: `1px solid ${theme.palette.primary.main}40`,
          boxShadow: `
            0 28px 80px rgba(0, 0, 0, 0.18),
            0 16px 48px ${theme.palette.primary.main}25,
            0 8px 24px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            inset 0 -1px 0 rgba(255, 255, 255, 0.25)
          `,
          transform: 'translateY(-8px) scale(1.02)',
          '&::before': {
            opacity: 1,
            height: '4px',
          },
        },
      }),
    },
    subtle: {
      ...baseStyles,
      backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.4)',
      boxShadow: `
        0 10px 32px rgba(0, 0, 0, 0.1),
        0 4px 12px rgba(0, 0, 0, 0.06),
        0 2px 8px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -1px 0 rgba(255, 255, 255, 0.1)
      `,
      ...(disableHover ? {} : {
        '&:hover': {
          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.65)' : 'rgba(255, 255, 255, 0.55)',
          backdropFilter: 'blur(25px) saturate(180%)',
          WebkitBackdropFilter: 'blur(25px) saturate(180%)',
          border: `1px solid ${theme.palette.primary.main}25`,
          boxShadow: `
            0 16px 48px rgba(0, 0, 0, 0.12),
            0 8px 24px ${theme.palette.primary.main}15,
            0 4px 12px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(255, 255, 255, 0.15)
          `,
          transform: 'translateY(-4px) scale(1.01)',
          '&::before': {
            opacity: 1,
          },
        },
      }),
    },
  };

  return variants[variant];
};

export const getGradientCardStyles = (theme: Theme, disableHover: boolean = false): SxProps => {
  const isDark = theme.palette.mode === 'dark';
  const primaryOpacity = isDark ? '20' : '12';
  const secondaryOpacity = isDark ? '15' : '10';
  
  return {
  background: `
    linear-gradient(135deg, ${theme.palette.primary.main}${primaryOpacity} 0%, ${theme.palette.primary.light}08 50%, ${theme.palette.secondary.main}${secondaryOpacity} 100%),
    radial-gradient(circle at 30% 30%, ${theme.palette.primary.main}10 0%, transparent 50%),
    radial-gradient(circle at 70% 70%, ${theme.palette.secondary.main}08 0%, transparent 50%)
  `,
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: `1px solid ${theme.palette.primary.main}30`,
  borderRadius: '24px',
  boxShadow: `
    0 12px 40px rgba(0, 0, 0, 0.1),
    0 4px 16px ${theme.palette.primary.main}15,
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1)
  `,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, 
      ${theme.palette.primary.main}40 0%, 
      ${theme.palette.secondary.main}40 50%,
      ${theme.palette.primary.main}40 100%
    )`,
    opacity: 0.8,
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)'} 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    opacity: 0.6,
    zIndex: 0,
  },
  ...(disableHover ? {} : {
    '&:hover': {
      background: `
        linear-gradient(135deg, ${theme.palette.primary.main}${isDark ? '25' : '18'} 0%, ${theme.palette.primary.light}12 50%, ${theme.palette.secondary.main}${isDark ? '20' : '15'} 100%),
        radial-gradient(circle at 30% 30%, ${theme.palette.primary.main}15 0%, transparent 50%),
        radial-gradient(circle at 70% 70%, ${theme.palette.secondary.main}12 0%, transparent 50%)
      `,
      border: `1px solid ${theme.palette.primary.main}40`,
      boxShadow: `
        0 16px 48px rgba(0, 0, 0, 0.12),
        0 8px 24px ${theme.palette.primary.main}20,
        inset 0 1px 0 rgba(255, 255, 255, 0.6),
        inset 0 -1px 0 rgba(255, 255, 255, 0.15)
      `,
      transform: 'translateY(-6px) scale(1.01)',
      '&::before': {
        opacity: 1,
        height: '4px',
      },
    },
  }),
  };
};

